/**
 * @file Picks up a new deploy without a manual reload. The policy is shared with
 * ui-merchant, super-ui and consumer-ui: docs/decisions/0007-version-check-and-safe-reload.md.
 */

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS

const NIGHT_START_HOUR = 22
const NIGHT_END_HOUR = 5
const NIGHT_IDLE_MINUTES = 30
const MORNING_GAP_HOURS = 6
const CLOCK_TICK_MINUTES = 5
const CLOCK_JUMP_MINUTES = 5
export const RELOADED_FOR_SHA_KEY = 'blytz:reloaded-for-sha'
export const CHUNK_RELOADED_FOR_SHA_KEY = 'blytz:chunk-reloaded-for-sha'

const USER_INPUT_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'touchstart',
  'wheel'
]

/** A tick is the 5-minute clock; a wake is visible, focus, pageshow or a clock jump. */
export type ReloadTrigger = 'tick' | 'wake'

/** The one timing rule: when an automatic reload may disturb the user at all. */
export function isQuietMoment(
  at: Date,
  idleMs: number,
  trigger: ReloadTrigger
): boolean {
  const hour = at.getHours()
  const night =
    (hour >= NIGHT_START_HOUR || hour < NIGHT_END_HOUR) &&
    idleMs >= NIGHT_IDLE_MINUTES * MINUTE_MS
  const morning = trigger === 'wake' && idleMs >= MORNING_GAP_HOURS * HOUR_MS

  return night || morning
}

/** Local and dev builds carry no commit SHA, or a placeholder such as `dev`. */
export function isCommitSha(sha: string | undefined): boolean {
  return /^[0-9a-f]{7,40}$/i.test(sha ?? '')
}

function localDate(at: Date): string {
  return `${at.getFullYear()}-${at.getMonth() + 1}-${at.getDate()}`
}

/** The local date a night belongs to, or null in the day: 02:00 belongs to the evening before. */
export function nightOf(at: Date): string | null {
  const hour = at.getHours()
  if (hour >= NIGHT_START_HOUR) return localDate(at)
  if (hour >= NIGHT_END_HOUR) return null

  return localDate(new Date(at.getFullYear(), at.getMonth(), at.getDate() - 1))
}

export const BUILD_SHA = process.env.VUE_APP_BUILD_SHA ?? ''

export interface VersionCheckOptions {
  /** The SHA this bundle was built from. Anything but a commit SHA disables the check. */
  currentSha: string
  /** Resolves the deployed SHA, or null when it cannot be read. */
  fetchLatestSha: () => Promise<string | null>
  isUnsafe: () => boolean
  reload: () => void
  onUpdate: () => void
  /** The deployed build matches this one again, so any notice is stale. */
  onCurrent: () => void
  /** The deployed SHA still differs after the one reload this tab gets for it. */
  onStuck: (latestSha: string) => void
  storage?: Storage
  win?: Window
  now?: () => number
}

export interface VersionCheck {
  stop: () => void
  /** @returns false when an unsafe state needs the user to confirm first. */
  reloadNow: (confirmed?: boolean) => boolean
}

/** Reads `version.json` past every cache; any failure reads as "unknown". */
export async function fetchDeployedSha(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, { cache: 'no-store' })
    if (!response.ok) return null
    const body = await response.json()

    return typeof body?.sha === 'string' && body.sha ? body.sha : null
  } catch {
    return null
  }
}

/** Reading `window.sessionStorage` itself throws when the browser blocks site data. */
function sessionStorageOrNone(): Storage | undefined {
  try {
    return window.sessionStorage
  } catch {
    return undefined
  }
}

function readStorage(storage: Storage | undefined, key: string): string {
  try {
    return storage?.getItem(key) ?? ''
  } catch {
    return ''
  }
}

/** @returns false when the value was not stored, so no loop guard protects a reload. */
function writeStorage(
  storage: Storage | undefined,
  key: string,
  value: string
): boolean {
  if (!storage) return false
  try {
    storage.setItem(key, value)

    return storage.getItem(key) === value
  } catch {
    return false
  }
}

const INERT: VersionCheck = {
  stop: () => undefined,
  reloadNow: () => true
}

/**
 * Fetches `version.json` once a night, once at the morning moment, and right before a
 * reload. The 5-minute clock tick itself makes no request.
 */
export function startVersionCheck(options: VersionCheckOptions): VersionCheck {
  const {
    currentSha,
    fetchLatestSha,
    isUnsafe,
    reload,
    onUpdate,
    onCurrent,
    onStuck,
    storage = sessionStorageOrNone(),
    win = window,
    now = Date.now
  } = options
  if (!isCommitSha(currentSha)) return INERT

  const doc = win.document
  let latestSha: string | null = null
  let stuck = false
  let announced = false
  let evaluating = false
  let reloading = false
  let stopped = false
  let lastInputAt = now()
  let lastTickAt = now()
  // A night or morning check counts only once its fetch succeeds; a failure retries.
  let nightChecked: string | null = null
  let morningCheckedFor: number | null = null
  let morningFailedFor: number | null = null
  let lastFetch: { at: number; sha: string | null } | null = null
  let inFlight: Promise<string | null> | null = null

  /** A result from this same moment serves as the re-check, so it is not fetched twice. */
  function fetchFresh(): Promise<string | null> {
    if (lastFetch && now() - lastFetch.at < MINUTE_MS) {
      return Promise.resolve(lastFetch.sha)
    }
    inFlight ??= fetchLatestSha().then(
      sha => {
        lastFetch = { at: now(), sha }
        inFlight = null

        return sha
      },
      () => {
        inFlight = null

        return null
      }
    )

    return inFlight
  }

  /** @returns true while the deployed build still differs from this one. */
  function applyDeployedSha(sha: string | null): boolean {
    if (stopped || !sha) return false
    if (sha === currentSha) {
      if (announced) onCurrent()
      latestSha = null
      stuck = false
      announced = false

      return false
    }
    if (sha === latestSha) return true
    latestSha = sha
    stuck = readStorage(storage, RELOADED_FOR_SHA_KEY) === sha
    if (stuck) onStuck(sha)

    return true
  }

  /** @returns false when the fetch failed. */
  async function check(): Promise<boolean> {
    const sha = await fetchFresh()
    applyDeployedSha(sha)

    return sha !== null
  }

  /** An automatic reload needs its loop guard stored; the user's own click does not. */
  function reloadFor(sha: string, byUser = false) {
    if (!writeStorage(storage, RELOADED_FOR_SHA_KEY, sha) && !byUser) {
      stuck = true

      return
    }
    reloading = true
    reload()
  }

  async function evaluate(trigger: ReloadTrigger) {
    if (!latestSha || stuck || evaluating || reloading || stopped) return
    const inputAt = lastInputAt
    if (!isQuietMoment(new Date(now()), now() - inputAt, trigger)) return
    if (isUnsafe()) return
    evaluating = true
    let stillStale = false
    try {
      stillStale = applyDeployedSha(await fetchFresh())
    } finally {
      evaluating = false
    }
    if (stillStale && !stuck && lastInputAt === inputAt && !isUnsafe()) {
      reloadFor(latestSha)
    }
  }

  /** The notice shows only for a stale tab that did not reload at once. */
  function announce() {
    if (!latestSha || reloading || announced || stopped) return
    announced = true
    onUpdate()
  }

  /** Once per night, at the first tick or wake at or after 22:00; a failure retries. */
  async function nightCheck() {
    const night = nightOf(new Date(now()))
    if (night && night !== nightChecked && (await check())) {
      nightChecked = night
    }
  }

  /** A failed morning fetch retries on later ticks until the next input. */
  async function morningCheck() {
    const gap = lastInputAt
    const ok = await check()
    morningCheckedFor = ok ? gap : morningCheckedFor
    morningFailedFor = ok ? null : gap
  }

  async function onTick() {
    await nightCheck()
    // A retried morning check is still the morning moment: no input came since the gap.
    const morningRetry = morningFailedFor === lastInputAt
    if (morningRetry) await morningCheck()
    await evaluate(morningRetry ? 'wake' : 'tick')
    announce()
  }

  async function onWake() {
    await nightCheck()
    const idleMs = now() - lastInputAt
    if (
      idleMs >= MORNING_GAP_HOURS * HOUR_MS &&
      morningCheckedFor !== lastInputAt
    ) {
      await morningCheck()
    }
    await evaluate('wake')
    announce()
  }

  function onInput() {
    lastInputAt = now()
  }

  function onVisibilityChange() {
    if (doc.visibilityState !== 'hidden') void onWake()
  }

  const tickMs = CLOCK_TICK_MINUTES * MINUTE_MS
  const clockTimer = setInterval(() => {
    const elapsed = now() - lastTickAt
    lastTickAt = now()
    void (elapsed > tickMs + CLOCK_JUMP_MINUTES * MINUTE_MS
      ? onWake()
      : onTick())
  }, tickMs)

  doc.addEventListener('visibilitychange', onVisibilityChange)
  win.addEventListener('focus', onWake)
  win.addEventListener('pageshow', onWake)
  USER_INPUT_EVENTS.forEach(type =>
    doc.addEventListener(type, onInput, { capture: true, passive: true })
  )

  return {
    stop() {
      stopped = true
      clearInterval(clockTimer)
      doc.removeEventListener('visibilitychange', onVisibilityChange)
      win.removeEventListener('focus', onWake)
      win.removeEventListener('pageshow', onWake)
      USER_INPUT_EVENTS.forEach(type =>
        doc.removeEventListener(type, onInput, { capture: true })
      )
    },
    reloadNow(confirmed = false) {
      if (!confirmed && isUnsafe()) return false
      if (latestSha) {
        reloadFor(latestSha, true)
      } else {
        reload()
      }

      return true
    }
  }
}

/** Webpack's lazy-route failures: a deploy pruned the chunk this bundle names. */
export function isChunkLoadError(error: unknown): boolean {
  const { name, message } = (error ?? {}) as { name?: string; message?: string }

  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message ?? '')
  )
}

/**
 * Reloads once per running build when a chunk fails to load. Without storage for the
 * guard it never reloads, since nothing would stop a loop.
 * @returns false when it did not reload, so the caller shows the normal error.
 */
export function reloadForChunkError(
  currentSha: string,
  reload: () => void,
  storage: Storage | undefined = sessionStorageOrNone()
): boolean {
  const key = currentSha || 'unknown'
  if (readStorage(storage, CHUNK_RELOADED_FOR_SHA_KEY) === key) return false
  if (!writeStorage(storage, CHUNK_RELOADED_FOR_SHA_KEY, key)) return false
  reload()

  return true
}
